/**
 * Upcoming screen.
 *
 * Reads /api/tasks/upcoming (next 30 days, scheduled or next-status with a
 * scheduled_at). Buckets into Tomorrow / This week / Next week / Later
 * client-side.
 */

import { useTasksUpcoming } from '../api/tasks';
import type { TaskDto } from '../api/tasks';
import { TaskSection } from '../components/TaskSection';
import { useSectionSorts } from '../lib/task-sort';
import { useToggleTask } from './_use-open-task';
import { useTaskQuickActions } from './_use-task-quick-actions';
import styles from './Lists.module.css';

interface Buckets {
  tomorrow: TaskDto[];
  thisWeek: TaskDto[];
  nextWeek: TaskDto[];
  later: TaskDto[];
}

export function UpcomingScreen() {
  const query = useTasksUpcoming();
  const complete = useToggleTask();
  const quick = useTaskQuickActions();
  // Must be called before the early isLoading/isError returns below so React
  // sees a stable hook order on every render.
  const sorts = useSectionSorts({
    'upcoming.tomorrow': 'native',
    'upcoming.thisWeek': 'native',
    'upcoming.nextWeek': 'native',
    'upcoming.later': 'native',
  });

  if (query.isLoading) return <div className={styles.loading}>Loading…</div>;
  if (query.isError) return <div className={styles.error}>Couldn’t load upcoming.</div>;

  const tasks = query.data?.tasks ?? [];
  const buckets = bucket(tasks);
  const total = tasks.length;

  return (
    <>
      <div className={styles.head}>
        <h2 className={styles.title}>Upcoming</h2>
        <div className={styles.sub}>
          {total} task{total === 1 ? '' : 's'} scheduled in the next 30 days.
        </div>
      </div>

      <TaskSection
        title="Tomorrow"
        tasks={buckets.tomorrow}
        sort={sorts.sortFor('upcoming.tomorrow')}
        onSortChange={(next) => sorts.setSortFor('upcoming.tomorrow', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="This week"
        tasks={buckets.thisWeek}
        sort={sorts.sortFor('upcoming.thisWeek')}
        onSortChange={(next) => sorts.setSortFor('upcoming.thisWeek', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Next week"
        tasks={buckets.nextWeek}
        sort={sorts.sortFor('upcoming.nextWeek')}
        onSortChange={(next) => sorts.setSortFor('upcoming.nextWeek', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Later"
        tasks={buckets.later}
        sort={sorts.sortFor('upcoming.later')}
        onSortChange={(next) => sorts.setSortFor('upcoming.later', next)}
        onComplete={complete}
        quick={quick}
      />

      {total === 0 && <div className={styles.empty}>Nothing scheduled.</div>}
    </>
  );
}

function bucket(tasks: TaskDto[]): Buckets {
  const now = new Date();
  const startOfTomorrow = startOfDay(addDays(now, 1));
  const startOfNextWeek = startOfDay(addDays(startOfWeek(now), 7));
  const startOfWeekAfter = startOfDay(addDays(startOfNextWeek, 7));

  const out: Buckets = { tomorrow: [], thisWeek: [], nextWeek: [], later: [] };
  for (const t of tasks) {
    const at = t.scheduledAt ? Date.parse(t.scheduledAt) : null;
    if (!at) continue;
    if (sameDay(new Date(at), startOfTomorrow)) {
      out.tomorrow.push(t);
    } else if (at < startOfNextWeek.getTime()) {
      out.thisWeek.push(t);
    } else if (at < startOfWeekAfter.getTime()) {
      out.nextWeek.push(t);
    } else {
      out.later.push(t);
    }
  }
  return out;
}

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}
function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function startOfWeek(d: Date): Date {
  const c = startOfDay(d);
  const dow = c.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  c.setDate(c.getDate() + offset);
  return c;
}
function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}
